import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Label,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import UserRoleContainer from "./userroleContainer";
import UserRoleForm from "./userroleform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchUserRolesThunk,
  addUserRoleThunk,
  deleteUserRoleThunk,
  fetchRolesThunk,
  listAdminUserThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionEnum } from "../../../Components/constants/permissions";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  userRoles: rbac.userRoles,
  roles: rbac.roles,
  adminUsers: rbac.adminUsers,
  loading: rbac.loading,
}));

const UserRolePage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const permissions = usePermissions();
  const { userRoles, roles, adminUsers, loading } = useSelector(rbacSelector);
  const location = useLocation();

  const userRoleList = userRoles || [];

  const locationState = (location.state as any) || {};
  const initialUserId = locationState?.usermasterid || null;
  const shouldOpenModal = locationState?.openModal === true;
  const [selectedUserId, setSelectedUserId] = useState<number | null>(initialUserId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserRoleId, setSelectedUserRoleId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(
      listAdminUserThunk({
        page: 1,
        per_page: 100,
        usermasterid: 0,
        usertypeid: 0,
        username: null,
        status: 0,
        email_id: null,
        phone_no: null,
      })
    );
    dispatch(fetchRolesThunk());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUserId) {
      dispatch(fetchUserRolesThunk(selectedUserId));
    }
  }, [dispatch, selectedUserId]);

  useEffect(() => {
    if (shouldOpenModal && initialUserId && selectedUserId && adminUsers.length > 0) {
      setTimeout(() => {
        setIsModalOpen(true);
      }, 100);
    }
  }, [shouldOpenModal, initialUserId, selectedUserId, adminUsers.length]);

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedUserRoleId(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        selectedRoles: Yup.array()
          .of(Yup.number())
          .min(1, "At least one role is required")
          .required("At least one role is required"),
      }),
    []
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      roleid: "",
      selectedRoles: [] as number[],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!selectedUserId) {
        toast.error("Please select a user first");
        return;
      }

      if (!values.selectedRoles || values.selectedRoles.length === 0) {
        toast.error("Please select at least one role");
        return;
      }

      try {
        setIsModalOpen(false);
        let successCount = 0;
        let errorCount = 0;
        
        for (const roleid of values.selectedRoles) {
          try {
            const res = await dispatch(
              addUserRoleThunk({
                usermasterid: selectedUserId,
                roleid: Number(roleid),
              })
            );
            if (res?.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
            console.error("Error adding role:", err);
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} role(s) added successfully!`);
        }
        if (errorCount > 0) {
          toast.error(`${errorCount} role(s) failed to add`);
        }

        await dispatch(fetchUserRolesThunk(selectedUserId));

        resetForm();
      } catch (err) {
        console.error("Error saving user roles:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const handleRemoveRole = (roleId: number) => {
    const currentSelected = validation.values.selectedRoles || [];
    validation.setFieldValue(
      "selectedRoles",
      currentSelected.filter((id: number) => id !== roleId)
    );
  };

  const toggleModal = () => {
    if (!selectedUserId) {
      toast.error("Please select a user first");
      return;
    }
    if (!isModalOpen) {
      validation.resetForm();
    } else {
      validation.resetForm();
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const userOptions = adminUsers.map((user: any) => ({
    label: `${user.username} (${user.email_id})`,
    value: user.usermasterid,
  }));

  const selectedUserOption = userOptions.find((opt: any) => opt.value === selectedUserId) || null;

  const columns = useMemo(
    () => [
      {
        header: "User Role ID",
        accessorKey: "userroleid",
        cell: ({ row }: any) => {
          const id = row.original.userroleid;
          return (
            <div className="text-center">
              {`#ID${String(id).padStart(3, "0")}`}
            </div>
          );
        },
      },
      {
        header: "Username",
        accessorKey: "username",
        cell: (cell: any) => {
          const value = cell.getValue();
          return <div className="text-center">{value || "-"}</div>;
        },
      },
      {
        header: "Email ID",
        accessorKey: "email_id",
        cell: (cell: any) => {
          const value = cell.getValue();
          return <div className="text-center">{value || "-"}</div>;
        },
      },
      {
        header: "Phone Number",
        accessorKey: "phone_no",
        cell: (cell: any) => {
          const value = cell.getValue();
          return <div className="text-center">{value || "-"}</div>;
        },
      },
      {
        header: "Role Name",
        accessorKey: "rolename",
        cell: (cell: any) => {
          const value = cell.getValue();
          return <div className="text-center">{value || "-"}</div>;
        },
      },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="text-center">
              {value ? (
                <>
                  {handleValidDate(value)},{" "}
                  <small className="text-muted">{handleValidTime(value)}</small>
                </>
              ) : (
                <>-</>
              )}
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <div className="text-center">
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: statusColor,
                  border: `1px solid ${statusColor}`,
                  textTransform: "capitalize",
                }}
              >
                {value}
              </span>
            </div>
          );
        },
      },
      {
        header: "Created By",
        accessorKey: "createdby",
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="text-center">
              {value || "-"}
            </div>
          );
        },
      },
      ...(permissions.includes(PermissionEnum.UPDATE_USERROLE) || permissions.includes(PermissionEnum.DELETE_USERROLE) ? [{
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;

          return (
            <div className="d-flex justify-content-center align-items-center">
              {permissions.includes(PermissionEnum.DELETE_USERROLE) && (
                <Button
                  color=""
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row.userroleid);
                  }}
                  title="Delete"
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              )}
            </div>
          );
        },
      }] : []),
    ],
    [handleDeleteClick, permissions]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="User Role" pageTitle={["RBAC", "User Roles"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={6}>
            <Label className="fw-bold mb-2">
              {initialUserId ? "User" : "Select User"}
            </Label>
            <div style={{ width: "100%" }}>
              <Select
                options={userOptions}
                placeholder="Select User to View/Manage Roles"
                value={selectedUserOption}
                onChange={(e: { value: any }) => {
                  setSelectedUserId(e?.value || null);
                  setIsModalOpen(false);
                }}
                isDisabled={!!initialUserId}
                classNamePrefix="react-select"
              />
            </div>
          </Col>
          <Col md={6} className="text-end d-flex justify-content-end gap-2 align-items-center">
            {permissions.includes(PermissionEnum.VIEW_NEWUSER) && (
              <Button 
                color="primary" 
                outline 
                onClick={() => navigate('/usermaster')}
              >
                <i className="ri-user-line align-middle me-1" />
                Users
              </Button>
            )}
            {permissions.includes(PermissionEnum.ADD_USERROLE) && (
              <Button 
                color="primary" 
                onClick={toggleModal}
                disabled={!selectedUserId}
              >
                + Add New Role
              </Button>
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  selectedUserId ? (
                    userRoleList.length > 0 ? (
                      <UserRoleContainer
                        columns={columns}
                        data={userRoleList}
                        customPageSize={10000}
                        limit={10000}
                        setPage={() => {}}
                        onRowClick={handleRowClick}
                        tableClass="align-middle border-bottom rounded-pill"
                        theadClass="bg-primary text-white"
                        divClass="table-responsive table-card"
                      />
                    ) : (
                      <div className="text-center p-4">
                        No roles assigned to this user.
                      </div>
                    )
                  ) : (
                    <div className="text-center p-4">
                      Please select a user to view their roles.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          Add User Roles
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
              <UserRoleForm
                validation={validation}
                roles={roles}
                selectedUser={adminUsers.find((u: any) => u.usermasterid === selectedUserId)}
                isUserDisabled={!!initialUserId}
                selectedRoles={validation.values.selectedRoles || []}
                onRemoveRole={handleRemoveRole}
                existingUserRoles={userRoles.map((ur: any) => {
                  const role = roles.find((r: any) => r.rolename === ur.rolename);
                  return role?.roleid;
                }).filter((id: any): id is number => id !== undefined && id !== null)}
              />
          </ModalBody>
          <ModalFooter>
            {permissions.includes(PermissionEnum.ADD_USERROLE) && (
              <Button type="submit" color="primary" disabled={!validation.values.selectedRoles || validation.values.selectedRoles.length === 0}>
                Add Roles
              </Button>
            )}
          </ModalFooter>
        </form>
      </Modal>

      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete User Role"
        message="Are you sure you want to delete this User Role?"
        onConfirm={async () => {
          if (selectedUserRoleId && selectedUserId) {
            setDeleteModalOpen(false);
            const userroleid = selectedUserRoleId;
            setSelectedUserRoleId(null);
            try {
              const res = await dispatch(
                deleteUserRoleThunk(userroleid)
              );
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete user role");
                return;
              }
              toast.success("User role deleted successfully!");
              await dispatch(fetchUserRolesThunk(selectedUserId));
            } catch (err) {
              console.error("Error deleting user role:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default UserRolePage;
